/* Contrato do admin-push de notificações (ADR-0685). Staff compõe uma notificação (texto LIVRE) para
   TODOS os usuários (`audience='all'`) ou uma lista selecionada (`audience='users'` + userIds). O app puxa
   no launch (`/v1/notifications?since=`) e mapeia p/ uma AppNotification de origin='admin' — o texto livre
   vira PARAM de uma chave pass-through no app, mantendo tudo key-based lá. Compartilhado admin-service ↔
   admin-web; o app reusa a visão pública via seu client. */
import { z } from 'zod';

export const USER_NOTIFICATION_SEVERITIES = ['info', 'success', 'warning', 'error'] as const;
export const userNotificationSeveritySchema = z.enum(USER_NOTIFICATION_SEVERITIES);
export type UserNotificationSeverity = z.infer<typeof userNotificationSeveritySchema>;

export const USER_NOTIFICATION_AUDIENCES = ['all', 'users'] as const;
export const userNotificationAudienceSchema = z.enum(USER_NOTIFICATION_AUDIENCES);
export type UserNotificationAudience = z.infer<typeof userNotificationAudienceSchema>;

export const USER_NOTIFICATION_STATUSES = ['draft', 'published'] as const;
export const userNotificationStatusSchema = z.enum(USER_NOTIFICATION_STATUSES);
export type UserNotificationStatus = z.infer<typeof userNotificationStatusSchema>;

/* Filtro de vigência derivado de `expiresAt` vs. agora (não é coluna): `active` = sem
   expiração ou futura; `expired` = expiração no passado. Server-side pois a lista é paginada. */
export const USER_NOTIFICATION_EXPIRY_FILTERS = ['active', 'expired'] as const;
export const userNotificationExpiryFilterSchema = z.enum(USER_NOTIFICATION_EXPIRY_FILTERS);
export type UserNotificationExpiryFilter = z.infer<typeof userNotificationExpiryFilterSchema>;

const TITLE_MAX = 140;
const BODY_MAX = 2_000;
const ACTION_MAX = 300;
/* Cap defensivo do alvo segmentado (mesma ordem do fan-out do /promotion). */
export const USER_NOTIFICATION_MAX_RECIPIENTS = 100;

/** Visão do STAFF (admin-web) — inclui draft + metadados. */
export const userNotificationDto = z.object({
  id: z.string().min(1),
  type: z.string().min(1),
  severity: userNotificationSeveritySchema,
  title: z.string().min(1).max(TITLE_MAX),
  body: z.string().max(BODY_MAX).nullable(),
  audience: userNotificationAudienceSchema,
  action: z.string().max(ACTION_MAX).nullable(),
  status: userNotificationStatusSchema,
  recipientCount: z.number().int().nonnegative(),
  createdByStaffId: z.string().nullable(),
  publishedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  createdAt: z.string(),
});
export type UserNotification = z.infer<typeof userNotificationDto>;

/** Criação (staff). `userIds` obrigatório (≥1) quando `audience='users'`. */
export const createUserNotificationBody = z
  .object({
    type: z.string().min(1).default('admin.message'),
    severity: userNotificationSeveritySchema.default('info'),
    title: z.string().min(1).max(TITLE_MAX),
    body: z.string().max(BODY_MAX).optional(),
    audience: userNotificationAudienceSchema.default('all'),
    userIds: z.array(z.string().min(1)).max(USER_NOTIFICATION_MAX_RECIPIENTS).optional(),
    action: z.string().max(ACTION_MAX).optional(),
    /** ISO — expira do feed após esta data (opcional). */
    expiresAt: z.string().optional(),
  })
  .refine((v) => v.audience !== 'users' || (v.userIds !== undefined && v.userIds.length > 0), {
    message: 'user_notification_recipients_required',
    path: ['userIds'],
  });
export type CreateUserNotificationBody = z.infer<typeof createUserNotificationBody>;

/** Visão PÚBLICA (app pull) — só o necessário p/ renderizar; sem status/recipients. */
export const publicUserNotificationDto = z.object({
  id: z.string().min(1),
  type: z.string().min(1),
  severity: userNotificationSeveritySchema,
  title: z.string(),
  body: z.string().nullable(),
  action: z.string().nullable(),
  createdAt: z.string(),
});
export type PublicUserNotification = z.infer<typeof publicUserNotificationDto>;

/** Query da listagem de gestão (admin-web). Keyset `(createdAt,id)` desc: `cursor` opaco +
    `limit`. Os filtros são server-side por obrigação — com paginação o client só recebe a
    página atual (mesmo motivo documentado em `listCompaniesQuery`). `severity` é CSV dos enums
    (multi-seleção); `search` é "contém" no título (ILIKE); `expiry` deriva de `expiresAt`. */
export const listUserNotificationsQuery = z.object({
  search: z.string().min(1).max(TITLE_MAX).optional(),
  status: userNotificationStatusSchema.optional(),
  severity: z
    .string()
    .transform((s) => s.split(','))
    .pipe(z.array(userNotificationSeveritySchema))
    .optional(),
  audience: userNotificationAudienceSchema.optional(),
  expiry: userNotificationExpiryFilterSchema.optional(),
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ListUserNotificationsQuery = z.infer<typeof listUserNotificationsQuery>;

/** Página keyset da listagem de gestão (`items` + `nextCursor` opaco). */
export const userNotificationPageDto = z.object({
  items: z.array(userNotificationDto),
  nextCursor: z.string().nullish(),
});
export type UserNotificationPage = z.infer<typeof userNotificationPageDto>;

/* Lote destrutivo restrito a drafts (ADR-0820). Publicadas são inelegíveis no service mesmo que
   um cliente forje ids; não existe ação de publicar em massa. */
export const NOTIFICATION_BULK_MAX_IDS = 100;
export const bulkDeleteDraftNotificationsBody = z.object({
  ids: z
    .array(z.string().min(1))
    .min(1)
    .max(NOTIFICATION_BULK_MAX_IDS)
    .refine((ids) => new Set(ids).size === ids.length, { message: 'duplicate_bulk_ids' }),
  action: z.literal('deleteDrafts'),
});
export type BulkDeleteDraftNotificationsBody = z.infer<typeof bulkDeleteDraftNotificationsBody>;

export const bulkDeleteDraftNotificationsResponseDto = z.object({
  results: z.array(z.object({ id: z.string(), ok: z.boolean(), error: z.string().nullable() })),
});
export type BulkDeleteDraftNotificationsResponse = z.infer<
  typeof bulkDeleteDraftNotificationsResponseDto
>;

/** Feed do app: notificações novas desde o cursor + o novo cursor (ISO do mais recente). */
export const publicUserNotificationFeedDto = z.object({
  notifications: z.array(publicUserNotificationDto),
  cursor: z.string().nullable(),
});
export type PublicUserNotificationFeed = z.infer<typeof publicUserNotificationFeedDto>;
